//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'
import { dxButtonOptions } from 'devextreme/ui/button'

//#endregion

import { IndexedParamCacheStrategy } from '../../../data-provider/cache-strategies'

import { IMRToastOptions } from '@imr/controls.angularjs/services/toast'
import { Activity, Enums } from '../../../models/common'
import { CommonHelper, Dictionary, FileComponent, KeyType, LocalizationHelper, ReferenceTypeComponent, ToastHelper, TypeHelper } from '../../../helpers'
import { UserSessionService } from '../../../services/user-session'
import { AttachmentService } from '../../../services/attachments'

import { IMRBaseControlController } from '../../common/base'
import { IIMRToolbar } from '@imr/controls.angularjs/components/toolbar'
import { IMRComponentInitializedArgs } from '@imr/controls.angularjs/components/base'
import { IIMRPopup, IMRPopupOptions } from '@imr/controls.angularjs/components/overlay/popup'

import { IReferenceType } from '../../../models/util/interfaces'
import { IIMRTaskValidation, IMRTaskValidationOptions } from './task-validate.interfaces'
import { GridController, GridOptions, GridTypes } from '../../../modules/common/grid'
import { ColumnModel } from '../../../modules/api/api.models'
import { OpTaskProtocolVerification } from '../../../models/custom/op-task-protocol-verification'
import { OpTaskData } from '../../../models/core/op-task-data'
import { OpDataType, OpdataTypeEnum, OpFile } from '../../../models/core'
import { IIMRImage, IMRImageInitializedArgs, IMRImageOptions } from '../../../modules/common/image'

interface ProtocolVerification {
	id: number
	attribute: string
	expectedValue: string
	currentValue: string
}

export class IMRTaskValidationController extends IMRBaseControlController<IIMRTaskValidation, IMRTaskValidationOptions> implements IIMRTaskValidation {
	// #region Consts

	public get rejectionReason(): string {
		return LocalizationHelper.getLocalizedText(LocalizationHelper.Text.RejectionReason)
	}
	private get TaskValidationGridName(): string {
		return 'TaskValidationGrid'
	}
	private get AttributeName(): string {
		return 'attribute'
	}
	private get ExpectedValueName(): string {
		return 'expectedValue'
	}
	private get CurrentValueName(): string {
		return 'currentValue'
	}
	private get ATTACHMENTS_GRID_NAME(): string {
		return 'TASK_PROTOCOL_VERIFICATION_ATTACHMENTS_GRID_NAME'
	}

	//#endregion

	//#region members

	public popupOptions: IMRPopupOptions
	public gridOptions: GridOptions
	public textAreaOptions: DevExpress.ui.dxTextAreaOptions
	public declineValidationRules: DevExpress.ui.dxValidatorOptions
	public attachmentsGridOptions: GridOptions
	public isPreviewVisible: boolean = false
	public imageOptions: IMRImageOptions

	private popup: IIMRPopup
	private textArea: DevExpress.ui.dxTextArea
	private validator: DevExpress.ui.dxValidator
	private gridDeffer: JQueryDeferred<GridController<any>>
	private toolbar: IIMRToolbar
	private verificationStatus: Enums.TaskProtocolVerificationStatus
	private protocol: OpTaskProtocolVerification
	private attachmentsGrid: GridController<OpFile>
	private imageController: IIMRImage

	private referenceDataTypes: OpDataType[]
	//#endregion

	//#region Ctor

	constructor(private readonly userSessionService: UserSessionService, private readonly attachmentService: AttachmentService) {
		super()
	}

	//#endregion

	//#region Component

	protected initialize(options: IMRTaskValidationOptions): JQueryPromise<void> {
		this.initializeGridOptions()
		this.initializeTextAreaOptions()
		this.setImageOptions()

		return $.when(this.initializepPopupOptions())
	}

	//#endregion

	//#region IIMRDepositoryParameters implementation

	public show(opTaskProtocolVerification: OpTaskProtocolVerification): void {
		this.popup.showLoading()
		this.protocol = opTaskProtocolVerification

		if (this.protocol == null) {
			this.updateGridDataSource(new Dictionary([], KeyType.Number))
			ToastHelper.showWarning(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NoDataToVerify), <IMRToastOptions>{ displayTime: 5000 })
			this.popup.show()
			return
		}

		this.setAttachmentGridOptions(this.protocol)

		this.prepareDataSource(this.protocol)
		this.prepareTextAreaValue(this.protocol)

		//https://jira.aiut.com/browse/PG-355
		//this.disableWidjets(!this.isProtocolReadyToVerification(this.protocol))

		this.popup.show()
	}

	public hide(): void {
		this.popup.hide()
	}

	//#endregion

	//#region IIMRPopup Implementation

	private initializepPopupOptions(): JQueryPromise<any> {
		this.popupOptions = {
			innerOptions: {
				title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.TaskValidation),
				deferRendering: false,
				onHiding: () => this.onPopupHiding(),
				fullScreen: true,
			},
			bottomToolbarOptions: {
				items: this.getToolbarItems(),
				onInitialized: (e: IMRComponentInitializedArgs<IIMRToolbar>) => {
					this.toolbar = e.component
				},
			},

			onInitialized: (e: IMRComponentInitializedArgs<IIMRPopup>) => {
				this.initializePopup(e.component)
			},
		}

		return super.waitForOnInitialized(this.popupOptions)
	}

	private onPopupHiding(): void {
		this.popup.hideLoading()
		this.clear()
	}

	private initializePopup(component: IIMRPopup): void {
		this.popup = component
	}

	private getToolbarItems(): DevExpress.ui.dxToolbarItem[] {
		const toolbarItems: DevExpress.ui.dxToolbarItem[] = []

		const cancelButtonOptions: DevExpress.ui.dxButtonOptions = {
			hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Discard),
			text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Discard),
			stylingMode: 'outlined',
			type: 'danger',

			onClick: () => {
				this.verificationStatus = Enums.TaskProtocolVerificationStatus.Rejected
				this.validateAndSubmit()
			},
		}

		toolbarItems.push({
			location: 'after',
			widget: 'dxButton',
			options: cancelButtonOptions,
		})

		const acceptButtonOptions: DevExpress.ui.dxButtonOptions = {
			hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Accept),
			text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Accept),
			stylingMode: 'outlined',
			type: 'success',

			onClick: () => {
				this.verificationStatus = Enums.TaskProtocolVerificationStatus.Verified
				this.submit()
			},
		}

		toolbarItems.push({
			location: 'after',
			widget: 'dxButton',
			options: acceptButtonOptions,
		})

		return toolbarItems
	}

	//#endregion

	//#region Grid

	private initializeGridOptions(): void {
		this.gridDeffer = $.Deferred<GridController<any>>()
		this.gridOptions = {
			type: GridTypes.SMALL,
			name: this.TaskValidationGridName,
			buttonsOptions: {
				enabled: false,
			},
			columnsDictionary: this.getGridColumns(),
			allowColumnReordering: false,
			onInitialized: (e: any, grid: GridController<any>) => {
				this.gridDeffer.resolve(grid)
			},
			onCellPrepared: (e: any) => {
				this.onCellPrepared(e)
			},
		}
	}

	private onCellPrepared(e: any): void {
		const div: JQuery<HTMLElement> = $('<div>')
			.css('width', e.element.width - 20)
			.css('white-space', 'normal')
			.css('overflow-wrap', 'break-word')
			.css('display', 'inline')

		if (e.rowType == 'data' && e.column.dataField == this.ExpectedValueName) {
			const data: ProtocolVerification = e.data

			if (data != null) {
				if (_.size(this.referenceDataTypes) > 0) {
					const refDatatype: OpDataType = _.find(this.referenceDataTypes, (dt: OpDataType) => dt.idDataType == data.id)

					if (refDatatype != null) {
						if (data.expectedValue != null && +data.expectedValue >= 0) {
							e.cellElement.html('<div class="task-validate-grid-cell-loader"></div>')

							if (refDatatype.idReferenceType != Enums.ReferenceType.IdDataTypeEnum) {
								ReferenceTypeComponent.getReferenceObject(this.userSessionService.getDataProvider(), refDatatype.idReferenceType, +data.expectedValue)
									.then((referenceObject: IReferenceType) => {
										div.appendTo(e.cellElement).text(referenceObject == null ? null : referenceObject.toString())
									})
									.catch(() => {
										e.cellElement.html(div.text(data.expectedValue))
									})
							} else {
								this.userSessionService
									.getDataProvider()
									.System.getDictionaries([Enums.ReferenceType.IdDataTypeEnum])
									.then((dict: Dictionary<Dictionary<IReferenceType>>) => {
										const dictList: Dictionary<OpdataTypeEnum> = dict.tryGetTypedValue(Enums.ReferenceType.IdDataTypeEnum)

										if (dictList != null) {
											const dte: OpdataTypeEnum = _.find(dictList.values(), (item: OpdataTypeEnum) => {
												return item.idDataType == data.id && item.value == +data.expectedValue
											})

											if (dte != null) {
												div.appendTo(e.cellElement).text(dte.toString())
											}
										} else {
											div.appendTo(e.cellElement).text(data.expectedValue)
										}
									})
							}
						} else {
							e.cellElement.html(div.text(data.expectedValue))
						}
					} else {
						e.cellElement.html(div.text(data.expectedValue))
					}
				} else {
					e.cellElement.html(div.text(data.expectedValue))
				}
			}
		}

		if (e.rowType == 'data' && e.column.dataField == this.CurrentValueName) {
			const data: ProtocolVerification = e.data

			if (data != null) {
				const deferred: JQueryDeferred<string> = $.Deferred<string>()

				if (_.size(this.referenceDataTypes) > 0) {
					const refDatatype: OpDataType = _.find(this.referenceDataTypes, (dt: OpDataType) => dt.idDataType == data.id)

					if (refDatatype != null) {
						if (data.currentValue != null && !isNaN(+data.currentValue) && +data.currentValue >= 0) {
							e.cellElement.html('<div class="task-validate-grid-cell-loader"></div>')

							if (refDatatype.idReferenceType != Enums.ReferenceType.IdDataTypeEnum) {
								ReferenceTypeComponent.getReferenceObject(this.userSessionService.getDataProvider(), refDatatype.idReferenceType, +data.currentValue)
									.then((referenceObject: IReferenceType) => {
										deferred.resolve(referenceObject == null ? data.currentValue : referenceObject.toString())
									})
									.catch(() => {
										deferred.resolve(data.currentValue)
									})
							} else {
								this.userSessionService
									.getDataProvider()
									.System.getDictionaries([Enums.ReferenceType.IdDataTypeEnum])
									.then((dict: Dictionary<Dictionary<IReferenceType>>) => {
										const dictList: Dictionary<OpdataTypeEnum> = dict.tryGetTypedValue(Enums.ReferenceType.IdDataTypeEnum)

										if (dictList != null) {
											const dte: OpdataTypeEnum = _.find(dictList.values(), (item: OpdataTypeEnum) => {
												return item.idDataType == data.id && item.value == +data.currentValue
											})

											if (dte != null) {
												deferred.resolve(dte.toString())
											}
										}

										deferred.resolve(data.currentValue)
									})
							}
						} else {
							deferred.resolve(data.currentValue)
						}
					} else {
						deferred.resolve(data.currentValue)
					}
				} else {
					deferred.resolve(data.currentValue)
				}

				deferred
					.promise()
					.then((value: string) => {
						// e.cellElement.html(value)
						e.cellElement.html(div.text(value))
					})
					.catch(() => {
						// e.cellElement.html(data.currentValue)
						e.cellElement.html(div.text(data.currentValue))
					})
					.always(() => {
						e.cellElement.prepend(' ')
						e.cellElement.prepend(this.getComparingSpanElement(data))
					})
			}
		}
	}

	private getComparingSpanElement(data: ProtocolVerification): JQuery<HTMLElement> {
		const span: JQuery<HTMLElement> = $('<span />')
		span.css('font-weight', 'bold')

		if (data.currentValue == data.expectedValue) {
			span.addClass('icon-check')
			span.css('color', '#009900')
		} else {
			span.addClass('icon-close')
			span.css('color', 'red')
		}

		return span
	}

	private getGridColumns(): ColumnModel[] {
		const attribute: ColumnModel = new ColumnModel()
		attribute.name = this.AttributeName
		attribute.idName = this.AttributeName
		attribute.type = Enums.DataTypeClass.Text
		attribute.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Attribute)

		const expectedValue: ColumnModel = new ColumnModel()
		expectedValue.name = this.ExpectedValueName
		expectedValue.idName = this.ExpectedValueName
		expectedValue.type = Enums.DataTypeClass.Text
		expectedValue.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExpectedValue)

		const currentValue: ColumnModel = new ColumnModel()
		currentValue.name = this.CurrentValueName
		currentValue.idName = this.CurrentValueName
		currentValue.type = Enums.DataTypeClass.Text
		currentValue.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.EnteredValue)

		return [attribute, expectedValue, currentValue]
	}

	//#endregion

	//#region TextArea

	private initializeTextAreaOptions(): void {
		this.initializeDeclineValidationRulles()

		this.textAreaOptions = {
			height: 76,

			onInitialized: (e: any) => {
				this.textArea = e.component
			},
		}
	}

	private initializeDeclineValidationRulles(): void {
		this.declineValidationRules = {
			validationRules: [
				{
					type: 'required',
					message: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ReasonIsRequired),
				},
			],
			onInitialized: (e: any) => {
				this.validator = e.component
			},
		}
	}

	//#endregion

	//#endregion

	//#region  Attacments

	private setAttachmentGridOptions(protocol: OpTaskProtocolVerification): void {
		const attachmentsColumns: ColumnModel[] = this.attachmentService.getAttachmentGridColumns()
		attachmentsColumns.push(this.getAttachmentButtonColumn())

		this.attachmentsGridOptions = {
			name: this.ATTACHMENTS_GRID_NAME,
			idReferenceType: Enums.ReferenceType.IdFile,
			type: GridTypes.NO_SEARCH_BAR_SMALL,
			buttonsOptions: {
				enabled: false,
			},
			addCustomFilters: true,
			dataSource: protocol.relatedAttachment,
			columnsDictionary: attachmentsColumns,

			allowColumnReordering: !this.userSessionService.getLoggedOperator().hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.TaskAttachments),
			onRowDoubleClick: () => this.onAttachmentDownload(),
			onInitialized: (e: any, attachmentsGrid: GridController<OpFile>) => {
				this.attachmentsGrid = attachmentsGrid
				this.attachmentsGrid.refresh()
			},
			onSelectionChanged: (e: any) => this.showPreview(e),
		}
	}

	private getAttachmentButtonColumn(): ColumnModel {
		const column: ColumnModel = <any>{
			id: 'attachmentObject',
			idName: 'attachmentObject',
			cellTemplate: (container, options) => {
				let div: JQuery<HTMLElement> = $('<div class="attachment-object-container"></div>')
				div.appendTo(container)

				let buttonOptions: dxButtonOptions = {
					hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.GetAttachment),
					icon: 'fa fa-download',
					onClick: (e: any) => {
						this.onAttachmentDownload()
					},
				}
				let button: JQuery<HTMLElement> = $('<div class="attachment-object-button small-button dx-button-has-icon"></div>')
				button.dxButton(buttonOptions)
				button.appendTo(div)
			},
			caption: '',
			name: '',
			referenceTypeId: null,
			sortDesc: false,
			type: 0,
			unitId: null,
			unitName: '',
			where: null,
			isEditable: false,
			alignment: 'center',
			allowSorting: false,
			width: 50,
			allowResizing: false,
		}
		return column
	}

	private setImageOptions() {
		this.imageOptions = {
			onInitialized: (e: IMRImageInitializedArgs) => {
				this.imageController = e.component
			},
		}
	}

	private showPreview(e: any): void {
		this.isPreviewVisible = _.size(e.selectedRowsData) > 0

		if (this.attachmentsGrid != null) {
			this.imageController.setImage(this.attachmentsGrid.selectedItem)
		}
	}

	//#endregion

	//#region LifeCycle

	private isProtocolReadyToVerification(protocol: OpTaskProtocolVerification): boolean {
		if (protocol.applicationIndexNbr == null) {
			ToastHelper.showWarning(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NoDataToVerify), <IMRToastOptions>{ displayTime: 5000 })
			//https://jira.aiut.com/browse/PG-352
			// return false
		}

		if (
			(protocol.statusTaskData != null && protocol.statusTaskData.value == Enums.TaskProtocolVerificationStatus.Verified) ||
			(protocol.statusTaskData != null && protocol.statusTaskData.value == Enums.TaskProtocolVerificationStatus.Rejected)
		) {
			ToastHelper.showWarning(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.DataAlreadyVerificated), <IMRToastOptions>{ displayTime: 5000 })

			return false
		}

		return true
	}

	private prepareTextAreaValue(protocol: OpTaskProtocolVerification): void {
		if (protocol.cancelationTaskData != null) {
			this.textArea.option({ value: protocol.cancelationTaskData.value })
		}
	}

	private prepareDataSource(protocol: OpTaskProtocolVerification): void {
		this.popup.showLoading()
		const dataSourceDictionary: Dictionary<ProtocolVerification> = new Dictionary([], KeyType.Number)

		const idDataTypes: number[] = []
		idDataTypes.push(..._.map(protocol.dataFromServer, (item: OpTaskData) => item.idDataType))
		idDataTypes.push(..._.map(protocol.dataFromApplication, (item: OpTaskData) => item.idDataType))

		if (protocol != null) {
			this.userSessionService
				.getDataProvider()
				.DataType.getDataTypes(_.unique(idDataTypes))
				.then((dataTypes: OpDataType[]) => {
					this.referenceDataTypes = _.filter(dataTypes, (dataType: OpDataType) => dataType.idReferenceType != null)

					_.forEach(protocol.dataFromServer, (data: OpTaskData) => {
						const dataType: OpDataType = _.find(dataTypes, (dt: OpDataType) => dt.idDataType == data.idDataType)
						const value: ProtocolVerification = {
							id: data.idDataType,
							attribute: dataType != null ? dataType.toString() : data.idDataType.toString(),
							expectedValue: this.prepareValueForGrid(data, dataType),
							currentValue: null,
						}

						dataSourceDictionary.addOrUpdate(data.idDataType, value)
					})

					_.forEach(protocol.dataFromApplication, (data: OpTaskData) => {
						let value: ProtocolVerification = dataSourceDictionary.tryGetValue(data.idDataType)
						const dataType: OpDataType = _.find(dataTypes, (dt: OpDataType) => dt.idDataType == data.idDataType)

						if (value != null) {
							value.currentValue = this.prepareValueForGrid(data, dataType)
						} else {
							value = {
								id: data.idDataType,
								attribute: dataType != null ? dataType.toString() : data.idDataType.toString(),
								expectedValue: null,
								currentValue: this.prepareValueForGrid(data, dataType),
							}
						}
						dataSourceDictionary.addOrUpdate(data.idDataType, value)
					})

					this.updateGridDataSource(dataSourceDictionary)
				})
		} else {
			this.updateGridDataSource(dataSourceDictionary)
		}
	}

	private prepareValueForGrid(data: OpTaskData, dataType: OpDataType): any {
		return dataType != null && (!TypeHelper.isNullOrEmpty(data.value) || data.value == false)
			? CommonHelper.cellFormatForDifferentValues(this.userSessionService, { value: data.value, typeId: dataType.idDataTypeClass }, dataType.idDataTypeClass, dataType.idDataType)
			: data.value
	}

	private updateGridDataSource(dataSourceDictionary: Dictionary<ProtocolVerification>): void {
		this.gridDeffer.promise().then((grid: GridController<any>) => {
			grid.dxGrid.option({ dataSource: dataSourceDictionary.values() })
			this.popup.hideLoading()
		})
	}

	private disableWidjets(disable: boolean): void {
		if (this.toolbar != null) {
			this.toolbar.getInnerToolbar().option({ disabled: disable })
		}

		if (this.textArea != null) {
			this.textArea.option({ readOnly: disable })
		}
	}
	//#endregion

	//#region Events

	private clear(): void {
		this.textArea.reset()
		this.validator.reset()
		this.verificationStatus = null
		this.attachmentsGridOptions = null
		this.attachmentsGrid = null

		if (this.imageController != null) {
			this.imageController.setImage(null)
		}
	}

	private validate(): boolean {
		if (this.validator != null) {
			const result: DevExpress.ui.dxValidatorResult = this.validator.validate()

			if (result != null) {
				return result.isValid
			}
		}

		return true
	}

	private validateAndSubmit(): void {
		if (this.validate()) {
			this.submit()
		}
	}

	private submit(): void {
		this.popup.showLoading()
		let reason: string = null

		if (this.verificationStatus === Enums.TaskProtocolVerificationStatus.Rejected) {
			reason = this.textArea.option('value')
		}

		if (_.isFunction(this.options.onSubmited)) {
			this.options
				.onSubmited(this.verificationStatus, this.protocol.applicationIndexNbr, reason)
				.then(() => {
					this.hide()
				})
				.catch(() => {
					this.popup.hideLoading()
				})
		} else {
			this.popup.hideLoading()
		}
	}

	private onAttachmentDownload(): void {
		if (this.attachmentsGrid.selectedItem != null) {
			FileComponent.OpenFileSaver(this.attachmentsGrid.selectedItem)
		}
	}

	//#endregion
}
